import React, { useEffect, useState } from 'react';
import PopoutWindow from '../common/PopoutWindow';

const API_BASE_URL = '/api/documentation/jobs/';

const getAuthHeaders = () => {
    return {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
        'Content-Type': 'application/json'
    };
};

const getFileNameFromUrl = (url) => {
    if (!url) return 'Unknown File';
    return url.substring(url.lastIndexOf('/') + 1);
};

const formatSize = (size) => {
    if (!size) return '-';
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
    return (size / (1024 * 1024)).toFixed(2) + ' MB';
};

const openPreview = (url, name = 'file') => {
    if (!url) return;
    const previewUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    window.open(previewUrl, '_blank', 'width=900,height=700');
};

const APP_FONT = "'Arial', sans-serif";

const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    fontFamily: APP_FONT
};

const thStyles = {
    border: '1px solid #d0d0d0',
    padding: '8px 10px',
    textAlign: 'left',
    background: '#f0f0f0',
    fontWeight: 600,
    color: '#333'
};

const tdStyles = {
    border: '1px solid #e0e0e0',
    padding: '8px 10px',
    verticalAlign: 'top',
    color: '#333'
};

const viewButtonStyles = {
    fontSize: 12,
    background: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '3px 10px',
    cursor: 'pointer'
};

const AttachmentsTable = ({ items }) => {
    return (
        <div style={{ border: '1px solid #d0d0d0', borderRadius: 4 }}>
            <table style={tableStyles}>
                <thead>
                    <tr>
                        <th style={{ ...thStyles, width: 40, textAlign: 'center' }}>S/N</th>
                        <th style={{ ...thStyles, width: '22%' }}>DOCUMENT TYPE</th>
                        <th style={{ ...thStyles, width: 80, textAlign: 'center' }}>VIEW</th>
                        <th style={thStyles}>DOCUMENT DETAILS</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => {
                        const doc = item.doc;
                        const url = doc.document_file_url || doc.document_file || '';
                        const fileName = getFileNameFromUrl(url);
                        return (
                            <tr key={`${item.type}-${doc.id}`}>
                                <td style={{ ...tdStyles, textAlign: 'center' }}>{index + 1}</td>
                                <td style={tdStyles}>{doc.document_type || 'Document'}</td>
                                <td style={{ ...tdStyles, textAlign: 'center' }}>
                                    {url ? (
                                        <button
                                            type="button"
                                            onClick={() => openPreview(url, fileName)}
                                            style={viewButtonStyles}
                                            title="View Document"
                                        >
                                            View
                                        </button>
                                    ) : (
                                        <span style={{ color: '#999', fontSize: 11 }}>-</span>
                                    )}
                                </td>
                                <td style={tdStyles}>
                                    <div style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>{fileName}</div>
                                    <div style={{ fontSize: 11, color: '#555' }}>
                                        Size: {formatSize(doc.file_size)}
                                        {" | "}
                                        Uploaded: {doc.created_at ? new Date(doc.created_at).toLocaleDateString() + ' ' + new Date(doc.created_at).toLocaleTimeString() : '-'}
                                        {" | "}
                                        By: {doc.uploaded_by_name || '-'}
                                    </div>
                                    {item.type === 'payable' && doc.payable_reference && (
                                        <div style={{ fontSize: 11, color: '#28a745', marginTop: 4 }}>
                                            Payable Reference: {doc.payable_reference}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const groupByDepartment = (items) => {
    const groups = {};
    items.forEach(item => {
        const dept = item.doc.uploaded_by_department || 'Unknown';
        if (!groups[dept]) groups[dept] = [];
        groups[dept].push(item);
    });
    return groups;
};

const DepartmentSection = ({ name, items, collapsed, onToggle }) => (
    <div style={{ marginBottom: 20, border: '1px solid #ddd', borderRadius: 4, padding: '10px', background: '#fff' }}>
        <h4
            onClick={onToggle}
            style={{ margin: 0, color: '#007bff', fontSize: 15, textTransform: 'uppercase', fontFamily: APP_FONT, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: collapsed ? 0 : 8, borderBottom: collapsed ? 'none' : '1px solid #eee' }}
            title="Click to collapse/expand"
        >
            <span style={{ display: 'inline-block', width: 12, transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                ▼
            </span>
            {name}
        </h4>
        {!collapsed && <AttachmentsTable items={items} />}
    </div>
);

const GroupedAttachments = ({ items, collapsedDepartments, onToggle }) => {
    const groups = groupByDepartment(items);
    const departments = Object.keys(groups).sort();
    return (
        <>
            {departments.map(dept => (
                <DepartmentSection
                    key={dept}
                    name={dept}
                    items={groups[dept]}
                    collapsed={!!collapsedDepartments[dept]}
                    onToggle={() => onToggle(dept)}
                />
            ))}
        </>
    );
};

const DepartmentAttachmentsPopout = ({ jobId, department, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [collapsedDepartments, setCollapsedDepartments] = useState({});

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(
                    `${API_BASE_URL}${jobId}/department-attachments/?department=${encodeURIComponent(department)}`,
                    { headers: getAuthHeaders() }
                );
                if (!res.ok) throw new Error(`Failed to load attachments (${res.status})`);
                const json = await res.json();
                if (!cancelled) setData(json);
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [jobId, department]);

    const allItems = data
        ? [
            ...(data.job_documents || []).map(doc => ({ doc, type: 'job' })),
            ...(data.payable_documents || []).map(doc => ({ doc, type: 'payable' })),
            ...(data.do_attachments || []).map(doc => ({ doc, type: 'do' }))
        ]
        : [];

    const isAll = department.toUpperCase() === 'ALL';
    const displayDepartment = isAll ? 'All' : department;
    const title = `${displayDepartment.toUpperCase()} ATTACHMENTS - JOB ${jobId}`;

    return (
        <PopoutWindow
            title={title}
            name={`department-attachments-${jobId}-${department}`}
            onClose={onClose}
        >
            <div style={{ padding: '15px', fontFamily: APP_FONT, color: '#333' }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: 8, fontSize: 18, textTransform: 'uppercase', fontFamily: APP_FONT }}>
                    {displayDepartment} Attachments
                </h3>

                {loading && <div style={{ fontSize: 13, color: '#555', fontFamily: APP_FONT }}>Loading attachments...</div>}
                {error && <div style={{ color: 'red', fontSize: 13, fontFamily: APP_FONT }}>{error}</div>}

                {!loading && data && (
                    <>
                        {allItems.length > 0 ? (
                            isAll ? (
                                <div style={{ overflowY: 'auto', maxHeight: '75vh', padding: '0 4px 4px 0' }}>
                                    <GroupedAttachments
                                        items={allItems}
                                        collapsedDepartments={collapsedDepartments}
                                        onToggle={(dept) => setCollapsedDepartments(prev => ({ ...prev, [dept]: !prev[dept] }))}
                                    />
                                </div>
                            ) : (
                                <div style={{ overflowY: 'auto', maxHeight: '75vh', padding: '0 4px 4px 0' }}>
                                    <AttachmentsTable items={allItems} />
                                </div>
                            )
                        ) : (
                            <div style={{ fontSize: 13, color: '#555', padding: '10px 0', fontFamily: APP_FONT }}>
                                No attachments found for this department.
                            </div>
                        )}
                    </>
                )}
            </div>
        </PopoutWindow>
    );
};

export default DepartmentAttachmentsPopout;
